import useLocalize from '../../utils/hooks/useLocalize';
import ArrowsSortIcon, { SORT_STATE } from './ArrowsSortIcon';
import './ScanResultsTable.scss';
import { BodyCell, HeaderCell, SymbolCell } from './ScanTableCells';
import { useScanColumns } from './scanTableColumns';
import { formatNumber } from './scanTableFormatters';
import useScanSort from './useScanSort';

// Re-export for backward compatibility
export { formatNumber };

const ScanResultsTable = ({
  scanResults = [],
  isLoading = false,
  onRowClick,
  onSymbolClick,
  symbolsMetadata = {}
}) => {
  const localize = useLocalize();
  const { symbolColumn, scrollableColumns, scrollableGridTemplate } =
    useScanColumns(scanResults, localize);
  const { sortStates, handleSort, sortedResults } = useScanSort(scanResults);

  // ─── Loading / empty states ──────────────────────────────────────────────

  if (isLoading) {
    return (
      <div
        className="scan-results-table-container"
        role="table"
        aria-label={localize('scan.result.title') || 'Scan Results'}
        aria-busy="true"
      >
        <div className="loading-state">
          {localize('scan.results.loading') || 'Loading scan results...'}
        </div>
      </div>
    );
  }

  if (!scanResults || scanResults.length === 0) {
    return (
      <div
        className="scan-results-table-container"
        role="table"
        aria-label={localize('scan.result.title') || 'Scan Results'}
      >
        <div className="empty-state">
          {localize('scan.results.empty') || 'No scan results to display'}
        </div>
      </div>
    );
  }

  const sortStateSymbol = symbolColumn
    ? sortStates[symbolColumn.key] || SORT_STATE.DEFAULT
    : SORT_STATE.DEFAULT;

  const ariaSortSymbol =
    sortStateSymbol === SORT_STATE.ASC
      ? 'ascending'
      : sortStateSymbol === SORT_STATE.DESC
      ? 'descending'
      : 'none';

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      className="scan-results-table-container"
      role="table"
      aria-label={localize('scan.result.title') || 'Scan Results'}
    >
      {/* ── Header row ──────────────────────────────────────────────────── */}
      <div className="table-header" role="rowgroup">
        <div className="table-header__row" role="row">
          {symbolColumn && (
            <div
              className="header-cell-fixed"
              role="columnheader"
              scope="col"
              aria-sort={ariaSortSymbol}
            >
              <div className="header-content">
                <span className="header-text">{symbolColumn.header}</span>
                <ArrowsSortIcon
                  sortState={sortStateSymbol}
                  onClick={() => handleSort(symbolColumn.key)}
                  ariaLabel={`Sort ${symbolColumn.header}`}
                />
              </div>
            </div>
          )}

          <div
            className="header-scrollable"
            role="presentation"
            style={{ gridTemplateColumns: scrollableGridTemplate }}
          >
            {scrollableColumns.map((col) => (
              <HeaderCell
                key={col.key}
                column={col}
                sortState={sortStates[col.key] || SORT_STATE.DEFAULT}
                onSort={() => handleSort(col.key)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Data rows ───────────────────────────────────────────────────── */}
      <div role="rowgroup">
        {sortedResults.map((result, index) => (
          <div
            key={result.symbol || result.id || index}
            className="table-row"
            role="row"
            onClick={() => onRowClick && onRowClick(result, index)}
          >
            {symbolColumn && (
              <SymbolCell
                result={result}
                symbolKey={symbolColumn.key}
                metadata={symbolsMetadata}
                localize={localize}
                onSymbolClick={onSymbolClick}
              />
            )}

            <div className="body-scrollable" role="presentation">
              <div
                className="body-cells-container"
                style={{ gridTemplateColumns: scrollableGridTemplate }}
              >
                {scrollableColumns.map((col) => (
                  <BodyCell key={col.key} column={col} result={result} />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScanResultsTable;
